import React, { FC } from "react";
import type { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { DayState, freeHoursLabel, partialKey, DayWindow, dayState } from "./availability";

/**
 * 1NOW-11176: the parts of Design B's calendar that every date picker on the car page shares (the daily
 * calendar, and the Hours, Dates, Weeks and Months pickers of tenants that rent by the hour or the week),
 * drawn by the `.cpb-cal` rules in styles/__car_page_b.scss.
 */

/**
 * One day. The state is data (`data-state`): a plain number, muted and struck when booked, with one small
 * dot when the car is out for part of the day. That day says its free hours in a tooltip (hover, desktop)
 * and, when tapped, in the line under the calendar.
 */
export const CalendarDay: FC<{
  day: number;
  date?: Date;
  state: DayState;
  hours?: string | null;
  /** Why a day that is not booked all day still can't be chosen (1NOW-11215). It reads as booked, with this as its tooltip. */
  note?: string | null;
  onTap?: (date: Date) => void;
}> = ({ day, date, state, hours, note, onTap }) => (
  <span
    className="cpb-day"
    data-state={state}
    onClick={date && state === "partial" ? () => onTap?.(date) : undefined}
    aria-label={
      date
        ? `${date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}${
            note ? `, ${note}` : state === "booked" ? ", booked" : state === "partial" && hours ? `, ${hours}` : ""
          }`
        : undefined
    }
  >
    {day}
    {(note || (state === "partial" && hours)) && (
      <span className="cpb-tip" role="tooltip">
        {note || `${hours!.charAt(0).toUpperCase()}${hours!.slice(1)}`}
      </span>
    )}
  </span>
);

/** Said on a day that has no pick-up or drop-off time left, in the tooltip and to a screen reader. */
export const NO_TIMES_NOTE = "No pick-up or return times this day";

/** The `renderDayContents` of every B calendar: this car's availability for the day, from the picker's own answers. */
export const dayContents =
  (
    fullyUnavailable: Date[],
    partial: Record<string, DayWindow[]>,
    company: any,
    onTap?: (date: Date) => void,
    /** A day the car or the branch leaves no time on: drawn as booked, with its reason. */
    hasNoTimes?: (date: Date) => boolean,
  ) =>
  (day: number, date?: Date) => {
    if (!date) return day;
    const state = dayState(date, fullyUnavailable, partial);
    if (hasNoTimes && (state === "partial" || state === "open") && hasNoTimes(date)) {
      return <CalendarDay day={day} date={date} state="booked" note={NO_TIMES_NOTE} />;
    }
    const hours = state === "partial" ? freeHoursLabel(partial[partialKey(date)], company) : null;
    return <CalendarDay day={day} date={date} state={state} hours={hours} onTap={onTap} />;
  };

/** The month name with plain chevrons: the previous one on the first month on show, the next on the last. */
export const calendarHeader =
  (monthsShown: number) =>
  ({ monthDate, customHeaderCount, decreaseMonth, increaseMonth }: ReactDatePickerCustomHeaderProps) => (
    <div className="cpb-cal-head">
      <button
        type="button"
        aria-label="Previous month"
        onClick={decreaseMonth}
        style={customHeaderCount === 0 ? undefined : { visibility: "hidden" }}
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>
      <span className="react-datepicker__current-month">
        {monthDate.toLocaleString("en-US", { month: "long", year: "numeric" })}
      </span>
      <button
        type="button"
        aria-label="Next month"
        onClick={increaseMonth}
        style={customHeaderCount === monthsShown - 1 ? undefined : { visibility: "hidden" }}
      >
        <ChevronRightIcon className="h-5 w-5" />
      </button>
    </div>
  );

/** Under the calendar: the hours of the limited-hours day that was tapped, and the one slim legend row. */
export const CalendarNote: FC<{ tapped: Date | null; hours: string | null }> = ({ tapped, hours }) => (
  <>
    <p className="mt-2 min-h-[20px] text-center text-[13px] text-text-color" data-testid="cpb-day-hours" aria-live="polite">
      {tapped && hours ? `${moment(tapped).format("ddd D")} · ${hours}` : ""}
    </p>
    <ul className="cpb-legend" data-testid="cpb-legend" aria-label="Calendar key">
      <li>
        <i className="cpb-legend-dot" />
        Limited hours
      </li>
      <li>
        <span className="cpb-legend-strike">12</span>
        Booked
      </li>
    </ul>
  </>
);
